import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { MassivePackingApiAdapter } from '../adapters';
import type { LocationsByProductApiResponse } from '../adapters/MassivePackingApiAdapter';

/**
 * Hook para precargar las ubicaciones de todos los productos de los grupos.
 * Esto evita la espera cuando el usuario hace clic en "Preparar pedidos".
 */
export const useProductLocations = (
  groups: Array<{ products: Array<{ productId: number }> }>,
) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new MassivePackingApiAdapter(httpClient);

  // Obtener todos los productos únicos de todos los grupos
  const uniqueProductIds = useMemo(() => {
    const productIds = new Set<number>();
    groups.forEach((group) => {
      group.products.forEach((product) => {
        productIds.add(product.productId);
      });
    });
    return Array.from(productIds);
  }, [groups]);

  // Precargar ubicaciones usando React Query
  const {
    data: locationsByProduct,
    isLoading,
    error,
  } = useQuery<Record<number, LocationsByProductApiResponse['data']>>({
    queryKey: ['massivePackingLocations', uniqueProductIds.sort().join(',')],
    queryFn: () => apiAdapter.getLocationsByProductIds(uniqueProductIds),
    enabled: uniqueProductIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
    gcTime: 10 * 60 * 1000, // 10 minutos de garbage collection
  });

  return {
    locationsByProduct: locationsByProduct || {},
    isLoadingLocations: isLoading,
    locationsError: error?.message || null,
  };
};
